import React from 'react'
import useSWR from 'swr'

import UserOption from '../../atoms/Dropdown/users'
import CheckboxDropdown from '../../atoms/CheckboxDropdown'
import SingleUser from '../UserSearchDropdown/SingleUser'

import { uniqueArray } from '../../../utilities'
import { embedsToQuery } from '../../../lib/api'
import { IMMUTABLE_SWR_CONFIG } from '@SonarCX/providers/ApiProvider'
import Signal from '@SonarCX/lib/signal'
import { addRecentUser, getRecentUsers } from '@SonarCX/lib/idb'
import { useUserAPI, DEFAULT_EMBEDS } from '../../../providers/UserAPIProvider'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import useDebouncedState from '../../../hooks/useDebouncedState'

type TypedUserSearchDropdownProps = Record<string, any> & {
  typeID: string | null
  multi?: boolean
  extraEmbeds?: string[]
}

const TypedUserSearchDropdown: React.FC<TypedUserSearchDropdownProps> = ({
  typeID,
  multi,
  extraEmbeds = [],
  onSelect = _id => {},
  ...props
}) => {
  const { allUsers, updateUsers, loadUser } = useUserAPI()
  const { env } = useEnvironment()

  const [search, setSearch] = useDebouncedState('', 300)
  const [recents, setRecents] = React.useState([])
  const [loadedInitialRecents, setLoadedInitialRecents] = React.useState(false)

  const { data, isLoading } = useSWR(() => {
    const embeds = uniqueArray(DEFAULT_EMBEDS, extraEmbeds)
    let query = embedsToQuery(embeds) + '&page=0&per_page=25'
    if (typeID) {
      query += `&type_id=${typeID}`
    }
    if (search.length >= 2) {
      query += `&keywords=${encodeURIComponent(search)}`
    }

    return `userSearch:${query}`
  }, IMMUTABLE_SWR_CONFIG)

  // Update user list with new users from search
  React.useEffect(() => {
    if (data) {
      updateUsers(data)
    }
  }, [updateUsers, data])

  // Load recent users
  React.useEffect(() => {
    recents.forEach(id => {
      loadUser(id)
    })
  }, [recents, loadUser])

  // Get recent users from IDB
  React.useEffect(() => {
    if (!env?.name) {
      return
    }

    const getStoredRecents = async () => {
      const storedRecents = await getRecentUsers({
        envName: env.name,
        userType: typeID
      })
      setRecents(storedRecents)
    }

    if (!loadedInitialRecents) {
      getStoredRecents()
      setLoadedInitialRecents(true)
    }

    window.SONAR_SIGNAL.on(Signal.recentUsers, getStoredRecents)

    return () => {
      window.SONAR_SIGNAL.off(Signal.recentUsers, getStoredRecents)
    }
  }, [env, loadedInitialRecents, typeID])

  const options = React.useMemo(() => {
    if (!data) {
      return recents
    }

    const ids = data.map(d => d.id)
    return uniqueArray(recents, ids)
  }, [data, recents])

  const renderOption = React.useCallback(
    id => {
      const opt = allUsers[id]
      if (opt) {
        return <UserOption key={id} id={id} opt={opt} />
      }
      return null
    },
    [allUsers]
  )

  const handleSelect = React.useCallback(
    id => {
      onSelect(id)

      if (!env?.name || !id) {
        return
      }

      addRecentUser({ envName: env.name, userType: typeID, userID: id })
    },
    [onSelect, env, typeID]
  )

  const ddProps = {
    options,
    renderOption,
    onSearch: setSearch,
    loading: isLoading,
    onSelect: handleSelect
  }

  if (multi) {
    // @ts-ignore
    return <CheckboxDropdown {...ddProps} {...props} />
  } else {
    // @ts-ignore
    return <SingleUser {...ddProps} {...props} />
  }
}

export default TypedUserSearchDropdown
